import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { translate } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { combineLatestWith, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { EventType, OrgDataSchema } from '@pwp-common';

import { PTableCol } from '../../../../../common/p-table/p-table-col';
import { EventRequestConfigService } from '../../../../../services/event/event-requests/event-request-config.service';
import { OrgDataService } from '../../../../../services/orgs/org-data/org-data.service';
import { SettingsEditorTableBase } from '../../../../generic/settings/abstract-classes/settings-editor-table-base';
import { CreateEventTypeDialogComponent } from '../../create-event-type-dialog/create-event-type-dialog.component';
import { EventRequestConfigEditorDialogComponent } from '../../event-request-config-editor-dialog/event-request-config-editor-dialog.component';
import { EventRequestConfigTableRow } from '../event-request-config-table-row/event-request-config-table-row';

@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-event-request-config-table',
  templateUrl: './event-request-config-table.component.html',
  styleUrls: ['./event-request-config-table.component.css'],
  encapsulation: ViewEncapsulation.None,
  providers: [DialogService, MessageService],
})
export class EventRequestConfigTableComponent
  extends SettingsEditorTableBase<EventRequestConfigTableRow>
  implements OnInit
{
  ///////////////////////////////////////////////////////////////////////
  // Constants
  ///////////////////////////////////////////////////////////////////////

  public cols: PTableCol[] = [
    { field: 'eventType', header: 'colEventType' },
    { field: 'rolePrimary', header: 'colPrimary' },
    { field: 'roleBackup', header: 'colBackup' },
    { field: 'edit', header: 'colEdit' },
  ];

  ///////////////////////////////////////////////////////////////////////
  // Lifecycle
  ///////////////////////////////////////////////////////////////////////

  constructor(
    private orgDataService: OrgDataService,
    private eventRequestConfigService: EventRequestConfigService,
    private changeDetectorRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private messageService: MessageService,
  ) {
    super();
  }

  ///////////////////////////////////////////////////////////////////////
  // Get Data
  ///////////////////////////////////////////////////////////////////////
  public getData(): void {
    this.loading = true;
    this.tableRows = this.eventRequestConfigService.getDocsArray().pipe(
      untilDestroyed(this),
      combineLatestWith(this.orgDataService.getOrgData()),
      map(([eventRequestConfig, orgData]) => {
        const orderedEventTypes = orgData.getEventTypes();
        const tableRows = orderedEventTypes.map((eventType) => {
          const eventRequestConfigData = eventRequestConfig.find((event) => event.getId() === eventType.getId());
          return new EventRequestConfigTableRow({ eventType, eventRequestConfigData });
        });
        this.loading = false;
        this.changeDetectorRef.detectChanges();
        return tableRows;
      }),
    );
  }

  public async createEventType(): Promise<void> {
    const orgData = await firstValueFrom(this.orgDataService.getOrgData());
    const dialogRef = this.dialogService.open(CreateEventTypeDialogComponent, {
      header: translate('create-event-type-dialog.header'),
      width: 'clamp(24rem, 50%, 36rem)',
      closeOnEscape: false,
      dismissableMask: false,
      data: {
        orgData,
      },
    });

    dialogRef.onClose.pipe(untilDestroyed(this)).subscribe((createdEventType: EventType | undefined) => {
      if (!createdEventType) {
        return;
      }

      this.showEventTypeCreated(createdEventType);
      this.refreshTable();
    });
  }

  public async editEventType(rowData: EventRequestConfigTableRow): Promise<void> {
    const orgData = await firstValueFrom(this.orgDataService.getOrgData());
    const dialogRef = this.dialogService.open(EventRequestConfigEditorDialogComponent, {
      header: translate('event-request-config-editor-dialog.header'),
      width: 'clamp(40rem, 85%, 75rem)',
      closeOnEscape: false,
      dismissableMask: false,
      data: {
        orgData,
        row: rowData,
      },
    });

    dialogRef.onClose.pipe(untilDestroyed(this)).subscribe((saved: boolean) => {
      if (saved) {
        this.refreshTable();
      }
    });
  }

  public async onRowsReordered(rows: EventRequestConfigTableRow[]): Promise<void> {
    const eventTypes = rows.map((row) => row.data.eventType);

    try {
      const orgData = await firstValueFrom(this.orgDataService.getOrgData());
      await this.orgDataService.updateDocFields({
        docId: orgData.getId(),
        data: { [OrgDataSchema.eventTypes]: eventTypes.map((eventType) => eventType.serialize()) },
      });
      this.showEventTypesReordered();
    } catch {
      this.showEventTypesReorderFailed();
      this.refreshTable();
    }
  }

  private refreshTable(): void {
    this.getData();
    this.changeDetectorRef.detectChanges();
  }

  private showEventTypeCreated(eventType: EventType): void {
    this.messageService.add({
      severity: 'success',
      summary: translate('create-event-type-dialog.saveSucceededSummary'),
      detail: translate('create-event-type-dialog.saveSucceededDetail', { displayName: eventType.getDisplayName() }),
    });
  }

  private showEventTypesReordered(): void {
    this.messageService.add({
      severity: 'success',
      summary: translate('event-request-config-table-editor.reorderSucceededSummary'),
      detail: translate('event-request-config-table-editor.reorderSucceededDetail'),
    });
  }

  private showEventTypesReorderFailed(): void {
    this.messageService.add({
      severity: 'error',
      summary: translate('event-request-config-table-editor.reorderFailedSummary'),
      detail: translate('event-request-config-table-editor.reorderFailedDetail'),
    });
  }
}
